// referral.js - Who sent this visitor, kept on the device until they fill the
// form in.
//
// Deliberately not part of analytics.js, and that is the whole reason this file
// exists rather than being four lines added there. Everything in analytics.js is
// switched off when somebody opts out of measurement, which is correct for an ad
// platform and wrong for this: an affiliate who sent us a customer is owed their
// commission whether or not that customer wants to be measured. A referral is a
// commercial fact about how the order arrived, not a tracking pixel.
//
// It is stored, not read off the URL at submit time, because a person lands on
// /a/4K7M2, reads three pages, and fills the form in twenty minutes later with
// nothing in the address bar. Ninety days, because a card in a wallet gets used
// weeks after it was handed over.
//
// Nothing here is ever shown. The customer never learns that a code was on their
// link, no page mentions it, and the confirmation says nothing about it.
//
// LEGEND
//   1. rememberReferral()  read the URL, keep what is there
//   2. referralCode()      what to post with the kit form, or nothing
//   3. forgetReferral()    after a successful submission
//   4. card serials        which printed card was scanned, if any

const KEY = "igs.ref.v1";
const MAX_AGE = 90 * 24 * 3600_000;

// The same alphabet the server generates from, so a mangled or malicious value
// never reaches the API. Length is bounded for the same reason.
const CODE = /^[0-9A-Z]{3,16}$/;

const clean = (value) => {
  const code = String(value || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 16);
  return CODE.test(code) ? code : "";
};

////////// 1. ARRIVAL //////////
// Two ways in, both handled here: the short link /a/CODE that an affiliate hands
// out, and ?ref=CODE on any page, which is what somebody pastes into a post.
//
// First one wins. Somebody who arrives on Dave's link, wanders off, and comes
// back through Sarah's a week later is Dave's: he is the one who did the work,
// and last-touch would make the two of them race each other over the same
// customer.

export function rememberReferral(pathname = "", search = "") {
  if (typeof window === "undefined") return "";

  const fromPath = /^\/a\/([^/?#]+)/i.exec(pathname || window.location.pathname);
  const fromQuery = new URLSearchParams(search || window.location.search).get("ref");
  const code = clean(fromPath ? fromPath[1] : fromQuery);
  if (!code) return referralCode();

  try {
    const held = referralCode();
    if (held) return held;
    localStorage.setItem(KEY, JSON.stringify({ code, at: Date.now() }));
  } catch {
    // A private window, or storage switched off. The referral is lost, which is
    // a worse outcome for the affiliate and no outcome at all for the customer,
    // so nothing here is allowed to throw in front of them.
  }
  return code;
}

////////// 2. WHAT THE FORM POSTS //////////

////////// 4. THE CARD THAT WAS SCANNED //////////
// A counter card carries a serial, and that serial is the only thing that can
// tie a parcel with no name on it to somebody who registered before posting it.
// Kept beside the referral and for the same reasons: it is a fact about how the
// order arrived rather than a measurement, and somebody scans a card in a shop
// and fills the form in that evening.
//
// It is not the referral and must not be folded into it. A card is one piece of
// paper; a code is an affiliate who has handed out hundreds.

const CARD_KEY = "igs.card.v1";
const SERIAL = /^[0-9A-Z]{4,12}$/;

export function rememberCard(serial) {
  if (typeof window === "undefined") return "";
  const clean = String(serial || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 12);
  if (!SERIAL.test(clean)) return cardSerial();
  try {
    localStorage.setItem(CARD_KEY, JSON.stringify({ serial: clean, at: Date.now() }));
  } catch { /* a private window loses it, and nothing here may throw */ }
  return clean;
}

export function cardSerial() {
  if (typeof window === "undefined") return "";
  try {
    const stored = JSON.parse(localStorage.getItem(CARD_KEY) || "null");
    if (!stored?.serial) return "";
    if (Date.now() - stored.at > MAX_AGE) {
      localStorage.removeItem(CARD_KEY);
      return "";
    }
    return SERIAL.test(stored.serial) ? stored.serial : "";
  } catch {
    return "";
  }
}

export function forgetCard() {
  if (typeof window === "undefined") return;
  try { localStorage.removeItem(CARD_KEY); } catch { /* nothing to do */ }
}

export function referralCode() {
  if (typeof window === "undefined") return "";
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!stored?.code) return "";
    if (Date.now() - stored.at > MAX_AGE) {
      localStorage.removeItem(KEY);
      return "";
    }
    return clean(stored.code);
  } catch {
    return "";
  }
}

////////// 3. AFTERWARDS //////////
// Cleared on a completed request so a second order from the same device months
// later is not credited to a link nobody clicked that time.

export function forgetReferral() {
  try { localStorage.removeItem(KEY); } catch { /* nothing to remove from */ }
}
